import { Injectable, inject } from '@angular/core';

import { Group as DefaultGroup, GridFixtureState, GridViewGroupingConfiguration, SixPackGroup } from '@cds/betting-offer/grouping/grid-view';
import { EpcotConfig, GridConfig, Sitecore } from '@frontend/sports/common/client-config-data-access';
import { Column, GridGrouping, GridLayout, GridSorting, Group, GroupBadge, SubscriptionTopic } from '@frontend/sports/grid/core/feature/model';
import { sortBy, times } from 'lodash-es';

export interface GridOption {
    activeGrouping?: number | string;
    marketGrouping?: boolean;
    moreGrouping?: boolean;
    grouping?: GridGrouping;
    disableGroupSorting?: boolean;
    sorting?: GridSorting;
    collapsedThreshold?: number;
    subscriptionTopic?: SubscriptionTopic;
    subscriptionTopicDetail?: string;
    groupOrder?: string[];
    badges?: Record<string, GroupBadge>;
    includeRetailGroups?: boolean;
    excludeDefaultMainMarket?: boolean;
}

export interface GridFixturesStatesCounts {
    live: number;
    preMatch: number;
}

@Injectable({ providedIn: 'root' })
export class ObservableGridProvider {
    protected sitecore = inject(Sitecore);
    protected epcotConfig = inject(EpcotConfig, { optional: true });
    protected gridConfig = inject(GridConfig, { optional: true });

    getColumns(
        config?: GridViewGroupingConfiguration,
        layout?: GridLayout,
        options?: GridOption,
        fixturesCount?: GridFixturesStatesCounts,
    ): Column[] {
        let source: (DefaultGroup | SixPackGroup)[] = [];

        const { groups, sixPackGroups } = config || {
            groups: new Array<DefaultGroup>(),
            sixPackGroups: new Array<SixPackGroup>(),
        };

        if (layout === GridLayout.SixPack) {
            source = this.applyGroupOrder(sixPackGroups, options);
        } else {
            source = this.applyGroupOrder(groups, options);
        }

        const maxColumns = this.epcotConfig?.isEnabled && this.gridConfig?.is2way3wayEnabled ? 2 : 4;

        const count = Math.min(maxColumns, source.length);

        if (count === 0) {
            return options?.excludeDefaultMainMarket ? [] : [this.getBaseColumn()];
        }

        return times(count).map((current, index) => this.getColumn(source, groups, index, options, fixturesCount));
    }

    protected getColumn(
        source: (DefaultGroup | SixPackGroup)[],
        groups: DefaultGroup[],
        active: number,
        options?: GridOption,
        fixturesStatesCount?: GridFixturesStatesCounts,
    ): Column {
        let mapped: Group[];

        mapped =
            source.length === 0
                ? [this.getBaseGroup()]
                : source
                      .filter(
                          (s) =>
                              !s.isFallbackGroup &&
                              (fixturesStatesCount ? this.IsGroupAllowedForGridFixturesState(s.gridFixtureState, fixturesStatesCount) : true),
                      )
                      .map((group, index) => this.getGroup(group, groups, index === active, options?.badges));

        return {
            id: (Date.now() + active).toString(32),
            groups: mapped,
            enabled: true,
            more: !!options?.moreGrouping,
        };
    }

    private getGroup(group: DefaultGroup | SixPackGroup, groups: DefaultGroup[], active: boolean, badges?: Record<string, GroupBadge>): Group {
        return {
            id: group.id,
            name: group.name,
            options: group.optionNames,
            balancedMarket: group.balancedMarketsEnabled,
            marketAttribute: group.showAttribute,
            childGroups: 'marketGroupIds' in group ? group.marketGroupIds : [],
            active,
            extended: false,
            visible: true,
            optionsToShow: group.optionsToShow,
            badge: badges?.[group.id],
            isFallbackGroup: group.isFallbackGroup,
            fallbackGridGroupIds: group.fallbackGridGroupIds,
        };
    }

    protected getBaseColumn(): Column {
        const group = this.getBaseGroup();

        return {
            id: 'main',
            groups: [group],
            enabled: true,
            more: false,
        };
    }

    protected getBaseGroup(): Group {
        return {
            id: 'main',
            name: this.sitecore.eventGrid.MarketSwitcher_MainMarket,
            active: true,
            extended: false,
            visible: true,
            isFallbackGroup: false,
            fallbackGridGroupIds: [],
        };
    }

    protected applyGroupOrder(source: (DefaultGroup | SixPackGroup)[], options?: GridOption): (DefaultGroup | SixPackGroup)[] {
        if (!options?.groupOrder?.length) {
            return source;
        }

        [...options.groupOrder].reverse().forEach((grid) => {
            // eslint-disable-next-line no-param-reassign
            source = sortBy(source, [(group: DefaultGroup | SixPackGroup) => grid !== group.id]);
        });

        return source;
    }

    private IsGroupAllowedForGridFixturesState(
        gridFixtureState: GridFixtureState | undefined,
        fixturesStatesCount: GridFixturesStatesCounts,
    ): boolean {
        switch (gridFixtureState) {
            case GridFixtureState.Any:
                return true;
            case GridFixtureState.LiveOnly:
                return fixturesStatesCount.preMatch === 0;
            case GridFixtureState.PrematchOnly:
                return fixturesStatesCount.live === 0;
            case GridFixtureState.Mix:
                return fixturesStatesCount.live > 0 && fixturesStatesCount.preMatch > 0;
            case GridFixtureState.PrematchAndMix:
                return fixturesStatesCount.preMatch > 0;
            case GridFixtureState.LiveAndMix:
                return fixturesStatesCount.live > 0;
            case undefined:
                return true;
            default:
                throw new Error(`Unknown grid fixture state: ${gridFixtureState}`);
        }
    }
}
